import { Alert, Box, Button, Paper, Typography } from '@mui/material';
import { useState } from 'react';
import { useDemoAppContext } from '../../pages/demo/DemoAppContext';
import Scrubber from './Scrubber';

interface TrackTrimmerProps {
  trackUrl: string;
  trackId: number;
  title?: string;
  artist?: string;
  release?: string;
  artworkUrl?: string;
}

const TrackTrimmer: React.FC<TrackTrimmerProps> = ({
  trackUrl,
  trackId,
  title,
  artist,
  release,
  artworkUrl,
}) => {
  const [selectedClip, setSelectedClip] = useState<{
    start: number;
    end: number;
  }>({ start: 0, end: 30 });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [clipId, setClipId] = useState<number | null>(null);
  const { client, clientId } = useDemoAppContext();

  const handleClipSelect = (start: number, end: number) => {
    setSelectedClip({ start, end });
    setError(null);
    setSuccess(null);
  };

  const handleCreateClip = async () => {
    if (!selectedClip) {
      setError('Please select a clip region first');
      return;
    }

    const response = await client.POST('/clip', {
      body: {
        clientId,
        start: selectedClip.start,
        end: selectedClip.end,
        trackId,
      },
    });

    if (response.error) {
      setError(response.error ?? 'An error occurred');
      setSuccess(null);
      return;
    }

    if (response.data) {
      if ('clipId' in response.data) {
        setClipId(response.data.clipId as number);
        setSuccess(`Clip created successfully!`);
        setError(null);
      } else {
        setError('Unexpected response format');
        setSuccess(null);
      }
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Scrubber
        trackUrl={trackUrl}
        title={title}
        artist={artist}
        release={release}
        artworkUrl={artworkUrl}
        onClipSelect={handleClipSelect}
        selectedClip={selectedClip}
      />
      {selectedClip && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateClip}
          >
            Create Clip
          </Button>
        </Box>
      )}
      {clipId && (
        <Paper
          elevation={1}
          sx={{
            mt: 2,
            p: 2,
            backgroundColor: 'primary.light',
            color: 'primary.contrastText',
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            Custom Clip ID: {clipId}
          </Typography>
        </Paper>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {success}
        </Alert>
      )}
    </Box>
  );
};

export default TrackTrimmer;
